import { Col, Row, Select, Table, Tooltip, Typography, Button } from "antd/es";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getQueryParams, generateQuery } from "../utilities/utility";
import {
  WAREHOUSE_FILTER_MAPPER,
  WAREHOUSE_MAPPER,
  allocationStatusMapper,
  filterOptions,
} from "./helpers";
import CsvDownload from "./CSVDownload";
import ReactDatePicker from "react-datepicker";
import { useOadData } from "./store";
import CardDiv from "modules/common/card-div/CardDiv";

const { Title } = Typography;

const AllocatedOrders = () => {
  const history = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sd, setSd] = useState(null);
  const [ed, setEd] = useState(null);
  const [tableFilter, setTableFilter] = useState({});
  const [filters, setFilters] = useState({
    page: parseInt(getQueryParams("page")) || 1,
    oda_type: getQueryParams("oda_type") || null,
    allocationStatus: "allocated",
    orderStatus: null,
    warehouse: null,
    orderPaymentType: null,
  });
  const { oadData, fetchOadOrders } = useOadData();

  useEffect(() => {
    setOrders(oadData);
  }, [oadData]);

  useEffect(() => {
    const filterItems = [{ ...filters }];
    history({ search: generateQuery(filterItems) });
  }, [filters, history]);

  useEffect(() => {
    if (getQueryParams("sd")) setSd(moment(getQueryParams("sd")));
    if (getQueryParams("ed")) setEd(moment(getQueryParams("ed")));
  }, []);

  const warehouseFilters = useMemo(() => {
    let filters = [];
    filters = WAREHOUSE_FILTER_MAPPER.slice(0, 3).map((el) => ({
      text: el.label,
      value: el.value,
    }));
    return filters;
  }, []);

  const handleTableChange = (page, filter) => {
    const _filters = {
      ...filters,
      page: page.current,
    };
    if ("orderStatus" in filter) {
      _filters["orderStatus"] = filter.orderStatus
        ? filter.orderStatus[0]
        : null;
      setTableFilter(filter);
    }
    if ("warehouse" in filter) {
      if (filter.warehouse) {
        _filters["warehouse"] = WAREHOUSE_MAPPER[filter.warehouse[0]]
          ? WAREHOUSE_MAPPER[filter.warehouse[0]]
          : "others";
      } else {
        _filters["warehouse"] = null;
      }
      setTableFilter(filter);
    }
    if ("orderPaymentType" in filter) {
      _filters["orderPaymentType"] = filter.orderPaymentType
        ? filter.orderPaymentType[0]
        : null;
      setTableFilter(filter);
    }
    setFilters(_filters);
  };

  const columns = [
    {
      title: "Order Id",
      dataIndex: "order_display_id",
      key: "order_display_id",
      render: (value, record) => (
        <div>
          <Link className="link" to={`/lead-details/${record.caseId}`}>
            {value}
          </Link>
        </div>
      ),
    },
    {
      title: "Warehouse",
      dataIndex: "warehouseId",
      key: "warehouse",
      width: "12%",
      filters: [...warehouseFilters],
      filterMultiple: false,
      filteredValue: tableFilter.warehouse || null,
      ellipsis: true,
      render: (value) => WAREHOUSE_MAPPER[value] || "Others",
    },
    {
      title: "Order Date",
      dataIndex: "createdAt",
      key: "created_at",
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (value) => moment(value).format("DD-MM-YYYY"),
    },
    {
      title: "City",
      dataIndex: "order_meta",
      key: "city",
      // render: value => value?.shipping_address?.city || null,
      render: (value) => (
        <Tooltip placement="topLeft" title={value?.shipping_address?.city}>
          {value?.shipping_address?.city}
        </Tooltip>
      ),
    },
    {
      title: "State",
      dataIndex: "order_meta",
      key: "province",
      ellipsis: true,
      width: "10%",
      render: (value) => (
        <Tooltip placement="topLeft" title={value?.shipping_address?.province}>
          {value?.shipping_address?.province}
        </Tooltip>
      ),
    },
    {
      title: "Pin Code",
      dataIndex: "order_meta",
      key: "pin_code",
      render: (value) => value?.shipping_address?.zip || null,
    },
    {
      title: "Order amount",
      dataIndex: "order_meta",
      key: "order_amount",
      render: (value) => value?.total_price || null,
    },
    {
      title: "Order Status",
      dataIndex: "orderStatus",
      key: "orderStatus",
      filters: [
        {
          text: "Shipping Pending",
          value: "shipping_pending",
        },
        {
          text: "Shipped",
          value: "shipped",
        },
      ],
      width: "10%",
      filterMultiple: false,
      filteredValue: tableFilter.orderStatus || null,
      ellipsis: true,
      render: (value) => (
        <Tooltip placement="topLeft" title={value}>
          {value === "shipping_pending" ? "Shipping Pending" : "Shipped"}
        </Tooltip>
      ),
    },
    {
      title: "Order type",
      dataIndex: "order_type",
      key: "orderPaymentType",
      filters: [
        {
          text: "cod",
          value: "cod",
        },
        {
          text: "prepaid",
          value: "pre_paid",
        },
      ],
      filterMultiple: false,
      filteredValue: tableFilter.orderPaymentType || null,
      render: (value) => {
        if (value === "pre_paid") return "PREPAID";
        else return "COD";
      },
    },
  ];

  const _getOtherOrders = useCallback(
    async (sd, ed) => {
      let _sd = null;
      let _ed = null;
      setLoading(true);
      if (!(!sd || sd === "" || ed === "" || !ed)) {
        _sd = moment(sd).toISOString();
        _ed = moment(ed).toISOString();
      }
      const res = await fetchOadOrders("others", filters, _sd, _ed);
      setLoading(false);
    },
    [filters]
  );

  const handleDateFilter = async () => {
    await _getOtherOrders(sd, ed);
  };

  useEffect(() => {
    _getOtherOrders(sd, ed);
  }, [_getOtherOrders, ed, sd]);

  return (
    <div className="pt-4">
      <CardDiv
        title={
          <div className="d-flex justify-content-between align-items-center">
            <div style={{ display: "flex", alignItems: "center" }}>
              <h4 style={{ marginRight: "10px" }}>Allocated Orders</h4>
              <CsvDownload data={orders} type="allocated" />
            </div>

            <div style={{ display: "flex" }}>
              <td style={{ position: "relative", zIndex: "999" }}>
                <ReactDatePicker
                  selected={sd}
                  onChange={(date) => setSd(date)}
                  formatWeekDay={(day) => day.slice(0, 3)}
                  className="form-control"
                  placeholderText="Start Date"
                />
              </td>
              <td style={{ position: "relative", zIndex: "999" }}>
                <ReactDatePicker
                  selected={ed}
                  onChange={(date) => setEd(date)}
                  formatWeekDay={(day) => day.slice(0, 3)}
                  className="form-control"
                  placeholderText="End Date"
                />
              </td>
              <Button
                type="primary"
                disabled={!sd || !ed}
                onClick={handleDateFilter}
                style={{ marginLeft: "2%" }}
              >
                Submit
              </Button>
            </div>
          </div>
        }
        component={
          <div style={{ minHeight: "75vh", maxHeight: "80vh", zIndex: "-1" }}>
            <div
              className="shadow table-row rounded-bottom scrollable-container"
              style={{
                fontSize: 13.2,
              }}
            >
              <div className="rounded-bottom">
                <Table
                  columns={columns}
                  onChange={(page, filter, sort) =>
                    handleTableChange(page, filter, sort)
                  }
                  loading={loading}
                  dataSource={orders || []}
                  scroll={{ y: 420 }}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default AllocatedOrders;
